import { useWallet } from '@txnlab/use-wallet';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import config from '../config';
import { shuffleTraitDependencies } from '../config/shuffleTraits';
import { useTraitShuffleFinalizeMutation, useTraitShuffleInitiateMutation } from '../hooks/useTraitShuffle';
import useTraitShuffleStore from '../hooks/useTraitShuffleStore';
import formatNumber from '../utils/formatNumber';
import { calculateTraitShuffleCosts } from '../utils/traitShuffleCosts';
import BalanceAlgo from './BalanceAlgo';
import BalanceTreats from './BalanceTreats';
import Button from './Button';
import FloatingActions from './FloatingActions';
import FullScreenLoader from './FullScreenLoader';
import NftImage from './NftImage';

const TraitShuffleFloatingActions = () => {
  const { activeAddress } = useWallet();
  const {
    activeTab,
    setActiveTab,
    selectedNft,
    selectedTrait,
    failedTraitImages,
    reset,
    initiateShuffleSuccess,
    setDismissedFlaggedTraits,
    dismissedFlaggedTraits,
  } = useTraitShuffleStore();
  const [costSliderValue, setCostSliderValue] = useState(0);
  const [showCostAdjuster, setShowCostAdjuster] = useState(false);
  const costAdjusterRef = useRef(null);

  // Function to handle clicks outside the cost adjuster to close it
  useEffect(() => {
    function handleClickOutside(event) {
      if (costAdjusterRef.current && !costAdjusterRef.current.contains(event.target)) {
        setShowCostAdjuster(false);
      }
    }

    // Add event listener when the overlay is shown
    if (showCostAdjuster) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Clean up event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCostAdjuster]);
  const { mutate: initiateSwap, isLoading } = useTraitShuffleInitiateMutation();
  const { mutate: finalizeSwap, isLoading: finalizeLoading } = useTraitShuffleFinalizeMutation();

  const finalizeIsEnabled = selectedNft && selectedTrait && failedTraitImages.length === 0;
  const allTraits = Object.entries(selectedNft?.metadata?.properties ?? {}).flat();
  const flaggedTraits = allTraits.filter((trait) => config.flaggableTraits.includes(trait[1]));
  const flaggedWithMultipleOptions = allTraits.filter((trait) => config.traitsWithMultipleOptions.includes(trait[1]));

  const hasSpecialTrait = allTraits.some((trait) => trait[0] === 'Special' && trait[1] !== 'None');
  const hasBannedTrait = allTraits.some((trait) => config.bannedTraits.includes(trait[1]));
  const bannedTraits = allTraits.filter((trait) => config.bannedTraits.includes(trait[1]));

  const categoryHasDependentTraits =
    selectedTrait && shuffleTraitDependencies[selectedTrait] && Object.keys(shuffleTraitDependencies[selectedTrait]).length > 0;
  const dependentTraitsInCategory = categoryHasDependentTraits ? Object.keys(shuffleTraitDependencies[selectedTrait]) : [];

  const onBack = () => {
    setActiveTab(0);
  };

  const onNext = () => {
    setActiveTab(1);
  };

  // Calculate adjusted costs based on slider value (0-100)
  const calculateAdjustedCosts = () => {
    return calculateTraitShuffleCosts(costSliderValue, config.traitShuffleTreatsCost, config.traitShuffleAlgoCost);
  };

  const adjustedCosts = calculateAdjustedCosts();

  const onInitiate = () => {
    if (!activeAddress) {
      toast.error('Please connect your wallet');
      return;
    }

    initiateSwap({
      address: activeAddress,
      selectedNft: selectedNft.id,
      selectedTrait: selectedTrait,
      costAdjustment: costSliderValue,
    });
  };

  const onFinalize = () => {
    if (!activeAddress) {
      toast.error('Please connect your wallet');
      return;
    }

    finalizeSwap({
      address: activeAddress,
      selectedNft: selectedNft.id,
      selectedTrait: selectedTrait,
      costAdjustment: costSliderValue,
    });
  };

  const onSubmit = () => {
    if (initiateShuffleSuccess) {
      onFinalize();
    } else {
      onInitiate();
    }
  };

  return (
    <>
      <FullScreenLoader loading={isLoading || finalizeLoading} label='Loading...' />
      <FloatingActions visible={selectedNft}>
        <div className='flex items-center flex-col md:flex-row justify-between gap-5 relative z-20'>
          <div className='flex items-center gap-6'>
            <div className='flex gap-4'>
              <div className='relative h-[60px] w-[60px] rounded-md bg-gray-400'>
                <div className='absolute left-[-8px] top-[-8px] z-30 flex h-[24px] w-[24px] items-center justify-center rounded-full border-2 border-white bg-primary-500'>
                  <p className='text-xs font-bold text-white'>{1}</p>
                </div>
                {selectedNft && <NftImage nft={selectedNft} className='absolute inset-0 rounded-md' />}
              </div>
            </div>

            {selectedNft && (
              <div className='flex flex-col text-lg w-[150px] relative'>
                <span className='font-medium text-gray-300'>Cost</span>
                <div className='flex gap-3 items-center'>
                  <BalanceTreats value={formatNumber(adjustedCosts.treats ?? 0)} />
                  <BalanceAlgo value={formatNumber(adjustedCosts.algo ?? 0)} />
                  <button
                    onClick={() => setShowCostAdjuster(!showCostAdjuster)}
                    className='ml-1 text-xs bg-gray-700 hover:bg-gray-600 rounded-full p-1 transition-colors'
                    title='Adjust cost mix'
                  >
                    <svg xmlns='http://www.w3.org/2000/svg' className='h-4 w-4' viewBox='0 0 20 20' fill='currentColor'>
                      <path
                        fillRule='evenodd'
                        d='M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z'
                        clipRule='evenodd'
                      />
                    </svg>
                  </button>
                </div>

                {showCostAdjuster && (
                  <div ref={costAdjusterRef} className='absolute bottom-full left-0 mb-2 bg-gray-800 rounded-lg p-4 shadow-lg z-30 w-[220px]'>
                    <div className='flex justify-between items-center mb-3'>
                      <h4 className='text-sm font-medium text-white'>Adjust Cost Mix</h4>
                      <button onClick={() => setShowCostAdjuster(false)} className='text-gray-400 hover:text-white'>
                        <svg xmlns='http://www.w3.org/2000/svg' className='h-4 w-4' viewBox='0 0 20 20' fill='currentColor'>
                          <path
                            fillRule='evenodd'
                            d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
                            clipRule='evenodd'
                          />
                        </svg>
                      </button>
                    </div>

                    <input
                      type='range'
                      min='0'
                      max='100'
                      value={costSliderValue}
                      onChange={(e) => setCostSliderValue(parseInt(e.target.value))}
                      className='w-full accent-primary-500 cursor-pointer'
                    />
                    <div className='flex justify-between text-xs text-gray-400 mt-1'>
                      <span>Default</span>
                      <span>+Treats/-Algo</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {selectedNft && selectedTrait && (
              <div className='flex flex-col text-lg ml-4'>
                <span className='font-medium text-gray-300'>Trait</span>
                <span className='text-white font-semibold'>{selectedTrait}</span>
              </div>
            )}
          </div>

          <div>
            {activeTab === 0 && (
              <div className='flex gap-4'>
                <Button theme='white' onClick={reset}>
                  Reset
                </Button>
                <Button disabled={!selectedNft} onClick={onNext}>
                  Next
                </Button>
              </div>
            )}
            {activeTab === 1 && (
              <div className='flex gap-4'>
                <Button theme='white' disabled={!selectedNft} onClick={onBack}>
                  Back
                </Button>
                <Button disabled={!finalizeIsEnabled} loading={isLoading || finalizeLoading} onClick={onSubmit}>
                  {initiateShuffleSuccess ? 'Finalize' : 'Initiate'}
                </Button>
              </div>
            )}
          </div>
        </div>

        {failedTraitImages.length > 0 && (
          <div className='absolute inset-0 flex w-full items-center justify-between md:rounded-full bg-red-500 px-8 z-50'>
            <div className='max-w-[300px]'>
              <p className='text-lg leading-tight text-gray-100'>Some trait images failed to load. Please contact support.</p>
            </div>

            <Button theme='white' onClick={reset}>
              Reset
            </Button>
          </div>
        )}

        {flaggedTraits.length > 0 && !dismissedFlaggedTraits && (
          <div className='absolute inset-0 z-30 flex w-full flex-col md:flex-row items-center justify-center gap-4 md:justify-between bg-red-500 px-8 md:rounded-full'>
            <div className='max-w-[350px]'>
              <p className='text-lg leading-tight text-gray-100'>
                WAIT: The {flaggedTraits.map((trait) => trait[1]).join(', ')} {flaggedTraits.length > 1 ? 'traits have' : 'trait has'} a high chance
                of clashing. Look over your kitty and ensure there are no clashes!
              </p>
            </div>
            <div className='flex gap-4'>
              <Button theme='outline-white' onClick={reset}>
                Reset
              </Button>

              <Button theme='outline-white' onClick={() => setDismissedFlaggedTraits(true)}>
                Continue
              </Button>
            </div>
          </div>
        )}

        {flaggedWithMultipleOptions.length > 0 && !dismissedFlaggedTraits && (
          <div className='absolute inset-0 z-30 flex w-full flex-col md:flex-row items-center justify-center gap-4 md:justify-between bg-red-500 px-8 md:rounded-full'>
            <div className='max-w-[350px]'>
              <p className='text-lg leading-tight text-gray-100'>
                WAIT: The {flaggedWithMultipleOptions.map((trait) => trait[1]).join(', ')}{' '}
                {flaggedWithMultipleOptions.length > 1 ? 'traits have' : 'trait has'} multiple styles. You may lose the original style.
              </p>
            </div>
            <div className='flex gap-4'>
              <Button theme='outline-white' onClick={reset}>
                Reset
              </Button>

              <Button theme='outline-white' onClick={() => setDismissedFlaggedTraits(true)}>
                Continue
              </Button>
            </div>
          </div>
        )}

        {hasSpecialTrait && (
          <div className='absolute inset-0 z-30 flex w-full flex-col md:flex-row items-center justify-center gap-4 md:justify-between bg-red-500 px-8 md:rounded-full'>
            <div className='max-w-[400px]'>
              <p className='text-lg leading-tight text-gray-100'>WAIT: This kitty has a Special trait. You cannot swap traits with this kitty!</p>
            </div>
            <div className='flex gap-4'>
              <Button theme='outline-white' onClick={reset}>
                Reset
              </Button>
            </div>
          </div>
        )}

        {hasBannedTrait && (
          <div className='absolute inset-0 z-30 flex w-full flex-col md:flex-row items-center justify-center gap-4 md:justify-between bg-red-500 px-8 md:rounded-full'>
            <div className='max-w-[500px]'>
              <p className='text-lg leading-tight text-gray-100'>
                WAIT: The {bannedTraits.map((trait) => trait[1]).join(', ')} {bannedTraits.length > 1 ? 'traits' : 'trait'} cannot be swapped.
              </p>
            </div>
            <div className='flex gap-4'>
              <Button theme='outline-white' onClick={reset}>
                Reset
              </Button>
            </div>
          </div>
        )}

        {categoryHasDependentTraits && !dismissedFlaggedTraits && (
          <div className='absolute inset-0 z-30 flex w-full flex-col md:flex-row items-center justify-center gap-4 md:justify-between bg-red-500 px-8 md:rounded-full'>
            <div className='max-w-[500px]'>
              <p className='text-lg leading-tight text-gray-100'>
                WAIT: Shuffling {selectedTrait} may affect existing traits. Some traits may be changed to accommodate the new {selectedTrait} trait.
              </p>
            </div>
            <div className='flex gap-4'>
              <Button theme='outline-white' onClick={reset}>
                Reset
              </Button>

              <Button theme='outline-white' onClick={() => setDismissedFlaggedTraits(true)}>
                Continue
              </Button>
            </div>
          </div>
        )}
      </FloatingActions>
    </>
  );
};

export default TraitShuffleFloatingActions;
